import { useState } from 'react';
import { Link } from 'react-router';
import { materialPath } from '../routes';
import { queueRefusalCopy } from '../upload-copy';
import {
  canCancel, canEdit, isActive, queueHeadline, titleFor, type UploadItem,
} from '../upload-queue';
import { formatBytes } from '../../core/file-facts';

/**
 * The queue, in the corner: every file being added, how far along it is, and what went wrong.
 *
 * Shaped after Project 180's upload panel. A panel rather than a toast, because three files
 * dropped at once are three things happening, and a toast says one thing and leaves. It stays
 * until it is closed once everything has finished, so a refusal that happened while the
 * teacher was reading something else is still there to read.
 *
 * What a row says is decided by its state alone (`upload-queue.ts`), and an error row says
 * the same sentence the single-file form would have said for that reason.
 */

export interface UploadPanelProps {
  items: readonly UploadItem[];
  onCancel: (id: string) => void;
  onEdit: (id: string, title: string) => void;
  onDismiss: () => void;
}

const percent = (item: UploadItem) =>
  item.progress.total > 0 ? Math.round((item.progress.sent / item.progress.total) * 100) : 0;

function StateLine({ item }: { item: UploadItem }) {
  switch (item.state) {
    case 'queued': return <>Waiting</>;
    case 'uploading': return <>{percent(item)}% of {formatBytes(item.size)}</>;
    case 'checking': return <>Checking the file…</>;
    case 'thumbnail': return <>Making a preview…</>;
    case 'done':
      return (
        <>
          Added
          {item.material && (
            <>
              {' · '}
              <Link
                className="link"
                to={materialPath(item.groupId, item.material.id)}
                aria-label={`Open ${item.material.title}`}
              >
                Open
              </Link>
            </>
          )}
        </>
      );
    case 'error': return <>{queueRefusalCopy(item.error ?? 'unknown')}</>;
    case 'cancelled': return <>Cancelled</>;
  }
}

export default function UploadPanel({ items, onCancel, onEdit, onDismiss }: UploadPanelProps) {
  const [collapsed, setCollapsed] = useState(false);
  if (items.length === 0) return null;

  const active = items.some(isActive);

  return (
    <section
      className={`upload-panel${collapsed ? ' is-collapsed' : ''}`}
      aria-label="Uploads"
    >
      <header className="upload-panel-head">
        <h2 aria-live="polite">{queueHeadline(items)}</h2>
        <button
          type="button"
          className="upload-panel-icon"
          aria-label={collapsed ? 'Show uploads' : 'Hide uploads'}
          aria-expanded={!collapsed}
          onClick={() => setCollapsed((was) => !was)}
        >
          <span aria-hidden="true">{collapsed ? '▴' : '▾'}</span>
        </button>
        {!active && (
          <button
            type="button"
            className="upload-panel-icon"
            aria-label="Close the upload list"
            onClick={onDismiss}
          >
            <span aria-hidden="true">×</span>
          </button>
        )}
      </header>

      {!collapsed && (
        <ul className="upload-panel-list">
          {items.map((item) => {
            const title = item.material?.title ?? titleFor(item);
            return (
              <li key={item.id} className={`upload-row is-${item.state}`}>
                <div className="upload-row-main">
                  {canEdit(item) ? (
                    <input
                      className="upload-row-title"
                      aria-label={`Title for ${item.name}`}
                      value={item.title}
                      onChange={(event) => onEdit(item.id, event.target.value)}
                    />
                  ) : (
                    <span className="upload-row-title" title={item.name}>{title}</span>
                  )}
                  <span className="upload-row-meta">
                    <span className="upload-row-size">{formatBytes(item.size)}</span>
                    {/* Every state change is said aloud, but not every percent. */}
                    <span
                      className="upload-row-state"
                      aria-live={item.state === 'uploading' ? 'off' : 'polite'}
                    >
                      <StateLine item={item} />
                    </span>
                  </span>
                  {item.state === 'uploading' && (
                    <progress
                      className="upload-row-bar"
                      aria-label={`Uploading ${title}`}
                      value={item.progress.sent}
                      max={Math.max(1, item.progress.total)}
                    />
                  )}
                </div>
                {canCancel(item) && (
                  <button
                    type="button"
                    className="secondary upload-row-cancel"
                    aria-label={`Cancel ${title}`}
                    onClick={() => onCancel(item.id)}
                  >
                    Cancel
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
